import { fetchJSON } from '@purple-dot/libraries/src/fetch';
import { appendResourceType } from '@purple-dot/libraries/src/url';
import { NextPage, NextPageContext } from 'next';
import { ComponentType } from 'react';
import { routeTo } from './components/common/linking';

/**
 * Wraps Next.js pages to work with our custom Express server (Next.js 13 compatible).
 *
 * This enables hybrid SSR/CSR with a custom server:
 * - SSR: Data comes from req._pageData (set by Express handler)
 * - CSR: Data comes from fetching the .json endpoint
 *
 * Architecture:
 * 1. Initial page load (SSR):
 *    User visits /merchant-portal/home
 *    → Express handler runs, attaches data to req._pageData
 *    → Next.js calls getInitialProps, reads from req._pageData
 *    → Page renders with data, HTML sent to browser
 *
 * 2. Client-side navigation (CSR):
 *    User clicks "Waitlists" link
 *    → Next.js fetches /merchant-portal/presales.json
 *    → getInitialProps receives JSON data
 *    → Page transitions smoothly without full reload
 *
 * @param Page - The Next.js page component to wrap
 * @returns Wrapped component with custom getInitialProps
 */
function expressPage<T>(Page: NextPage | ComponentType<T>) {
  function Wrapped(props: T): JSX.Element {
    return <Page {...props} />;
  }

  Wrapped.getInitialProps = async (ctx: NextPageContext) => {
    // SSR path: Running on server (initial page load)
    // The custom Express server has already run the handler and attached data to req._pageData
    if (ctx.req) {
      const serverData = (ctx.req as RequestWithPageMeta)?._pageData;

      // Return server data if available, otherwise fall back to query params
      // Pages without handlers will use query params (e.g., static pages, simple forms)
      return serverData ?? ctx.query ?? {};
    }

    // CSR path: Running in browser (client-side navigation)
    // Fetch the .json endpoint to get the same data the server would provide
    const path = appendResourceType('json', ctx.asPath ?? '/');

    try {
      const data = await fetchJSON(path);

      // Handle server-side redirects during client-side navigation
      if (data?._redirect) {
        const { pathname, query, path: dest } = data._redirect;
        void routeTo({ pathname, query }, dest);
        return {};
      }

      return data ?? {};
    } catch (err) {
      // If the .json endpoint doesn't exist (404), this page has no handler.
      // Fall back to query params (same as SSR fallback).
      // Support multiple error shapes: { statusCode }, { status }, axios-like { response.status }.
      const status =
        err?.statusCode ??
        err?.status ??
        err?.response?.status ??
        err?.cause?.statusCode ??
        err?.cause?.status;

      if (status === 404 || err?.code === 'ERR_BAD_REQUEST') {
        return ctx.query ?? {};
      }

      // Surface real errors (500, network issues, etc.)
      throw err;
    }
  };

  return Wrapped;
}

export default expressPage;
